<template>
  <div class="root">
    <div class="manuscript-editor">
      <div class="bottom sp">
        <button class="back sp" @click="handleBackAction">＜</button>
        <div ref="spSave" :data-disabled="!changed" class="sp save" @click="save">
          <img src="@/assets/img/icon/icon-save.png" />{{ $t("manuscriptPage.manuscriptEditor.save") }}
        </div>
        <div class="edit-manu">
          <img src="@/assets/img/icon/icon-edit-menu.png" @click="showMenu" />
        </div>
        <div class="text-counter" v-if="content !== null">
          {{ $t("manuscriptPage.manuscriptEditor.characterCount", { count: displayedContentLength }) }}
        </div>
      </div>
      <div class="main-content" :class="{ 'with-assist': isAssistPanelVisible && !isShowPreview && !isMobile }">
        <div class="manuscript-container">
          <manuscript-header
            ref="header"
            :title="title"
            :novelId="novelId"
            :manuscriptKey="manuscriptKey"
            :getContent="getLiveContent"
            :changed="changed"
            @changeTitle="onChangeTitle"
            @compositionStart="onStartComposition"
            @compositionEnd="onEndComposition"
            @post="onExportButtonClick(true)"
            @save="save"
            @commentIconClicked="handleCommentIconClick"
          />
          <div class="editor-container" :class="{ isShowPreview }">
            <editor
              ref="editor"
              :content="content"
              :isScriptMode="isScriptMode"
              @changeContent="onChangeContent"
              @typing="onTyping"
              @compositionStart="onStartComposition"
              @compositionEnd="onEndComposition"
            />
            <save-status-indicator class="save-status-indicator" :status="saveStatus" />
            <manuscript-preview
              v-if="isShowPreview"
              class="preview pc"
              :novelId="novelId"
              :manuscriptList="manuscriptList"
            />
          </div>
          <div class="bottom pc" :class="{ scriptVertical: isScriptMode && isVerticalWritingMode }">
            <div class="controller-container gap-1 ml-1">
              <undo-icon :fillColor="canUndo ? activeColor : defaultColor" @click="undo" />
              <redo-icon :fillColor="canRedo ? activeColor : defaultColor" @click="redo" />
              <hr class="vertical-hr" />
              <div class="editor-controller">
                <div class="insert-symbol">
                  <div class="icon-box" @click="brackets">
                    <icon-punctuation-mark />
                  </div>
                  <div class="icon-box" @click="ellipsis">
                    <icon-leader />
                  </div>
                  <div class="icon-box" @click="space">
                    <icon-space />
                  </div>
                  <div class="icon-box" @click="dash">
                    <component :is="dashIcon" />
                  </div>
                  <div v-if="!isScriptMode" class="icon-box" @click="ruby">
                    <icon-ruby />
                  </div>
                  <div v-if="!isScriptMode" class="icon-box" @click="emphasisMark">
                    <icon-side-point />
                  </div>
                  <div v-if="isScriptMode" class="icon-box" @click="insertPillar">
                    <icon-circle />
                  </div>
                  <div v-if="isScriptMode" class="icon-box" @click="insertTogaki">
                    <icon-togaki />
                  </div>
                  <div v-if="isScriptMode" class="icon-box" @click="insertFlashback">
                    <icon-flashback />
                  </div>
                </div>
              </div>
              <hr class="vertical-hr" />
              <div v-if="!isAssistPanelVisible" class="multi-editor-link-wrapper md">
                <button class="button default multi-editor-link" @click="goToMultiEditor">
                  {{ $t("manuscriptPage.manuscriptEditor.multiEditorMode") }}
                </button>
                <span
                  v-if="isFree"
                  class="multi-editor-premium-badge"
                  :data-tooltip="$t('exportManuscriptDialog.premiumOnly')"
                  @click="goToMultiEditor"
                >
                  <img src="@/assets/img/icon/premium.png" alt="Premium" />
                </span>
              </div>
              <div v-if="!isVerticalWritingMode" class="show-preview-wrapper">
                <div class="show-preview" :class="{ isShowPreview }" @click="onClickPreview">
                  <icon-preview-vertical />
                </div>
                <span
                  v-if="isFree"
                  class="premium-badge"
                  :data-tooltip="$t('exportManuscriptDialog.premiumOnly')"
                  @click="onClickPreview"
                >
                  <img src="@/assets/img/icon/premium.png" alt="Premium" />
                </span>
              </div>
            </div>
            <div class="setting-container">
              <dropup-menu
                class="dropup-menu"
                :title="countTypeName"
                :content="wordCountInfo"
                :items="countTypeList"
                :styleObject="{ backgroundColor: 'var(--color-surface-muted)' }"
              />
            </div>
            <proofreading-confirmation-dialog />
            <auto-indent-confirmation-dialog />
          </div>
        </div>
        <assist-panel class="assist-panel pc" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import clone from "lodash/cloneDeep";
import { debounce } from "lodash";
import UndoIcon from "icons/ArrowULeftTop.vue";
import RedoIcon from "icons/ArrowURightTop.vue";
import Editor, { IEditorEmitArgs } from "@/components/molecules/Editor.vue";
import ManuscriptPreview from "@/components/organisms/ManuscriptPreview.vue";
import AssistPanel from "@/components/organisms/AssistPanel.vue";
import DropupMenu, { Item } from "@/components/molecules/DropupMenu.vue";
import { Manuscript, ManuscriptSetting, Novel, Plan, SearchTextIndex } from "@/lib/models";
// NOTE: 機能紹介GIFを修正するため一時的にコメントアウト
// import { downloadPublicImage } from "@/lib/s3";
// import PremiumFeatureDialog, { PremiumFeatureDialogProps } from "@/components/ui/PremiumFeatureDialog.vue";
import { UserClient } from "@/lib/clients";
// dialog
import { Dialog } from "@/lib/utils";
import DialogVue from "@/components/ui/Dialog.vue";
import { showOkCancelDialog, openManuscriptEditorMenuDialog } from "@/lib/dialog";
import EpisodeListDialog, { EpisodeListDialogProps } from "@/components/ui/nolaNovel/EpisodeListDialog.vue";
import NolaNovelIntroduction from "@/components/ui/nolaNovel/NolaNovelIntroduction.vue";
import CupertinoAlertDialog, { CupertinoAlertDialogProps } from "@/components/ui/dialogs/CupertinoAlertDialog.vue";
import NolaNovelUpdateConfirm, {
  NolaNovelUpdateConfirmProps,
} from "@/components/ui/nolaNovel/NolaNovelUpdateConfirm.vue";
import EpisodeDelinkConfirm, { EpisodeDelinkConfirmProps } from "@/components/ui/nolaNovel/EpisodeDelinkConfirm.vue";
import ProofreadingConfirmationDialog from "@/components/ui/ProofreadingConfirmationDialog.vue";
import AutoIndentConfirmationDialog from "@/components/ui/AutoIndentConfirmationDialog.vue";
import RubyDialog, { RubyDialogProps } from "@/components/ui/RubyDialog.vue";
import EmphasisMarkDialog, { EmphasisMarkDialogProps } from "@/components/ui/EmphasisMarkDialog.vue";
import { NolaNovelUrlGenerator } from "@/lib/utils/generator/nolanovelUrl";
import ManuscriptHeader from "@/components/molecules/ManuscriptHeader.vue";
import SaveStatusIndicator, { SaveStatus } from "@/components/atoms/SaveStatusIndicator.vue";
import { createUrlWithUtmParams } from "@/lib/utils/url";
import { NolaAnalytics, NolaItemId, NolaPageName } from "@/lib/utils/analytics";
import AgentPromotionDialog from "@/components/ui/AgentPromotionDialog.vue";
import { handleShortcutKeys, TriggerKey } from "@/lib/utils/keyboardShortcuts";
import { useAgentStore, AgentStore } from "@/stores/agent";
import { MOBILE_BREAKPOINT } from "@/constants/breakpoints";
import { getEffectiveEditorTheme } from "@/lib/utils/theme";
import {
  reflowLogicalToVertical,
  unreflowVerticalToLogical,
  insertPillarTo,
  insertFlashbackTo,
  insertTogakiTo,
  stripSoftBreakMarker,
} from "@/lib/utils/scriptReflow";
import {
  countContentLength,
  countManuscriptDisplay,
  countOnlyCharacterWordCount,
  MANUSCRIPT_COUNT_DEBOUNCE_MS,
} from "@/lib/utils/manuscriptContentCount";
import { dashInputState, getDashPairText, loadDashInputSettings } from "@/lib/dashInputSettings";
import { BulkReplaceEvent } from "@/store/modules/properNoun";
import { setLastEditedManuscriptKey } from "@/lib/utils/lastEditedManuscript";
import IconPreviewVertical from "~icons/figma/PreviewVertical";
import IconPunctuationMark from "~icons/figma/PunctuationMark";
import IconLeader from "~icons/figma/Leader";
import IconSpace from "~icons/figma/Space";
import IconDash from "~icons/figma/Dash";
import IconHorizontalBar from "~icons/figma/HorizontalBar";
import IconRuby from "~icons/figma/Ruby";
import IconSidePoint from "~icons/figma/SidePoint";
import IconCircle from "~icons/figma/Circle";
import IconFlashback from "~icons/figma/Flashback";
import IconTogaki from "~icons/figma/Togaki";

const contentMaxLength = 100000;
const CONTENT_LENGTH_OVER_50000 = 50000;

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    Editor,
    ManuscriptPreview,
    AssistPanel,
    IconPreviewVertical,
    IconPunctuationMark,
    IconLeader,
    IconSpace,
    IconRuby,
    IconSidePoint,
    IconCircle,
    IconFlashback,
    IconTogaki,
    DropupMenu,
    ProofreadingConfirmationDialog,
    AutoIndentConfirmationDialog,
    UndoIcon,
    RedoIcon,
    ManuscriptHeader,
    SaveStatusIndicator,
  },

  props: {
    novelId: String,
    manuscriptKey: String,
    autoOpenHistory: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    const { onSelectCountType } = this as any;

    const countTypeList: Item[] = [
      {
        name: this.$t("manuscriptPage.manuscriptEditor.countTypes.allCharacters") as string,
        value: "onlyCharacter",
        callback() {
          onSelectCountType(this.value);
        },
      },
      {
        name: this.$t("manuscriptPage.manuscriptEditor.countTypes.withSpaces") as string,
        value: "addBlank",
        callback() {
          onSelectCountType(this.value);
        },
      },
      {
        name: this.$t("manuscriptPage.manuscriptEditor.countTypes.withNewlines") as string,
        value: "addNewLine",
        callback() {
          onSelectCountType(this.value);
        },
      },
      {
        name: this.$t("manuscriptPage.manuscriptEditor.countTypes.withSpacesAndNewlines") as string,
        value: "all",
        callback() {
          onSelectCountType(this.value);
        },
      },
    ];

    return {
      title: "",
      preTitle: "",
      content: "",
      preContent: "",
      // content / preContent が現在どちらの形式かを表す。脚本モードの縦横切替や
      // isScriptMode 変化時、両 watcher が同一 flush で発火してもリフローが二重適用
      // されないように、この状態を見て一度だけ変換する（reflow は冪等でないため）。
      scriptTextFormat: "logical" as "logical" | "vertical",
      saved: false,
      isSaving: false,
      // save() の多重呼び出しを直列化するための実行中Promise（次項参照）
      savePromise: null as Promise<void> | null,
      // 保存完了(チェックマーク)表示の2秒タイマーID。連続保存時に前回分をクリアするために保持する
      savedTimerId: null as ReturnType<typeof setTimeout> | null,
      countTypeList,
      hasNolaNovelAccount: false,
      defaultColor: "#a0a0a0",
      activeColor: "#5698e1",
      canUndo: false,
      canRedo: false,
      unbindShortcutKeys: null,
      agentStore: useAgentStore(),
      windowWidth: typeof window !== "undefined" ? window.innerWidth : 0,
      debouncedUpdateWindowWidth: null,
      // 作品全体の文字数をキャッシュ
      cachedTotalNovelContentLength: 0,
      // 他の原稿の文字数をキャッシュ（パフォーマンス最適化）
      otherManuscriptsBaseLength: 0,
      // 検索状態更新のデバウンス関数
      debouncedUpdateSearchState: null as ReturnType<typeof debounce> | null,
      displayedContentLength: 0,
      displayedPageCount: 0,
      isComposingText: false,
      debouncedUpdateCounts: null as ReturnType<typeof debounce> | null,
      nolaAnalytics: new NolaAnalytics(this.$gtm, this.$store),
      hasOpenedHistory: false,
    };
  },

  async created() {
    this._typingDirty = false;
    this.debouncedUpdateCounts = debounce(() => this.updateCountsNow(), MANUSCRIPT_COUNT_DEBOUNCE_MS);

    // 一覧表示時の優先取得フォールバック用に、最後に開いた原稿として記憶する
    setLastEditedManuscriptKey(this.novelId, this.manuscriptKey);

    // 優先取得: この原稿の本文を最優先で取得してから初期化する（未取得時のみ実際に取得する）
    // 直接URLアクセス直後は他の初期化リクエストと重なり一時的に失敗し得るため、
    // 失敗してもここで止めず initialize() には必ず進む（失敗時は本文が空のまま表示される）
    try {
      await this.$store.dispatch("manuscriptModule/loadManuscriptContent", {
        novelId: this.novelId,
        key: this.manuscriptKey,
      });
    } catch (error) {
      console.error(`Failed to load manuscript content for key: ${this.manuscriptKey}`, error);
    }
    // 取得後、バックグラウンド一括取得での上書きを防ぐため編集中として登録する
    this.$store.dispatch("manuscriptModule/registerEditingManuscript", this.manuscriptKey);

    const { initialize } = this;
    initialize();
  },

  mounted() {
    loadDashInputSettings();
    // デバウンシングされたウィンドウ幅更新関数を作成
    if (typeof window !== "undefined") {
      this.debouncedUpdateWindowWidth = debounce(this.updateWindowWidth, 150);
      window.addEventListener("resize", this.debouncedUpdateWindowWidth);
    }

    if (this.isScriptMode) {
      try {
        this.nolaAnalytics.logButtonEvent(NolaItemId.ScriptModeEditorOpened, this.manuscriptKey);
      } catch {
        // analytics エラーはエディタ初期化に影響させない
      }
    }
  },

  beforeDestroy() {
    // 編集中登録を解除する（他原稿への遷移・画面離脱時）
    this.$store.dispatch("manuscriptModule/unregisterEditingManuscript", this.manuscriptKey);

    // コンポーネントが破棄される前にショートカットキーを解除
    if (this.unbindShortcutKeys) this.unbindShortcutKeys();

    // ウィンドウリサイズイベントリスナーを削除
    if (typeof window !== "undefined" && this.debouncedUpdateWindowWidth) {
      window.removeEventListener("resize", this.debouncedUpdateWindowWidth);

      // デバウンシング関数のクリーンアップ
      this.debouncedUpdateWindowWidth.cancel();
    }

    // 検索状態更新のデバウンス関数のクリーンアップ
    if (this.debouncedUpdateSearchState) {
      this.debouncedUpdateSearchState.cancel();
    }

    if (this.debouncedUpdateCounts) {
      this.debouncedUpdateCounts.cancel();
    }
    this.clearLocalAutosave();
    this.flushLiveContent();

    // 保存完了表示タイマーのクリーンアップ
    if (this.savedTimerId !== null) {
      clearTimeout(this.savedTimerId);
    }
  },

  destroyed() {
    this.initializeSearchState();
  },

  watch: {
    // 同じ原稿を開いたまま登場シーンを切り替えた場合（:key が変わらず再生成されない）でも
    // カーソルを移動できるよう query.cursor の変化を監視する
    "$route.query.cursor": function watchSceneCursor() {
      this.$nextTick(() => this.moveCursorFromQuery());
    },
    // 他原稿の本文はバックグラウンドで遅延取得されるため、初期化時点ではまだ揃っていないことがある。
    // 全件取得が完了した時点で、他原稿の文字数キャッシュ（他原稿合計文字数）を再計算する。
    contentLoadState(newValue) {
      if (newValue === "loaded") {
        this.otherManuscriptsBaseLength = this.computeOtherManuscriptsLength();
        this.updateTotalNovelContentLength();
      }
    },
    // 一括置換が行われたら、開いている原稿の本文（未保存編集を含む）にも同じ置換を適用する。
    // ストアの本文で丸ごと上書きせず、同じ old→new 置換をローカルバッファに適用することで、
    // 未保存編集を保ったまま置換結果を反映する（保存/リロードでどちらも失われない）。
    bulkReplaceEvent(event: BulkReplaceEvent | null) {
      if (!event || !event.oldName) return;
      const { oldName, newName } = event;
      // 保存済みベースラインをストアの最新本文へ同期し、changed 判定を正しく保つ。
      // content より先に更新することで、content watcher 発火時の changed が最初から正確になる。
      const stored = this.manuscript;
      this.preContent = String((stored && stored.content) ?? this.content);
      this.content = this.content.replaceAll(oldName, () => newName);
      // 消費済みイベントはクリアし、ストアに古い置換イベントが残らないようにする
      this.$store.commit("properNounModule/setBulkReplaceEvent", null);
    },
    // AssistPanel表示状態の監視
    isAssistPanelVisible: {
      handler(newValue, oldValue) {
        if (newValue) {
          // AssistPanelが表示された時、プレビューが表示中ならプレビューを非表示に
          if (this.isShowPreview) {
            this.$store.dispatch("manuscriptModule/switchPreviewOnManuscriptEditor");
          }
        }
      },
      immediate: true,
    },
    // 脚本モードの切替監視: 表示形式（論理/縦書き）を同期する
    isScriptMode() {
      this.syncScriptTextFormat();
      this.updateCountsNow();
    },
    // 縦書きモード状態の監視
    isVerticalWritingMode: {
      handler(newValue) {
        // スマホ表示時は縦書きモード設定を無視
        if (!this.isMobile) {
          if (newValue) {
            // 縦書きモードがオンになった時、プレビューが表示中ならプレビューを非表示に
            if (this.isShowPreview) {
              this.$store.dispatch("manuscriptModule/switchPreviewOnManuscriptEditor");
            }
          }

          // 縦横切替に応じてテキスト形式を同期する
          this.syncScriptTextFormat();
        }
        this.updateCountsNow();
      },
    },
    // プレビュー状態変化の監視（AssistPanelとの排他制御）
    isShowPreview: {
      handler(newValue, oldValue) {
        // プレビューが表示されたらAssistPanelをVuex状態含めて非表示に
        if (newValue && this.isAssistPanelVisible) {
          this.$store.dispatch("assistNavigationModule/hidePanel");
        }
      },
    },

    content: {
      handler() {
        this.setModified(this.changed);
        // パフォーマンス最適化: 検索UIが表示されている場合のみ検索状態を更新
        // デバウンスを使用してキー入力のたびに正規表現処理を行うのを避ける
        if (this.enabledSearchReplace && this.debouncedUpdateSearchState) {
          this.debouncedUpdateSearchState();
        }
        if (!this.isComposingText && this.debouncedUpdateCounts) {
          this.debouncedUpdateCounts();
        }
      },
      immediate: true,
    },
    countType() {
      this.updateCountsNow();
    },
    manuscriptSetting: {
      handler() {
        this.updateCountsNow();
      },
      deep: true,
    },
    searchText: {
      handler() {
        // パフォーマンス最適化: 検索UIが表示されている場合のみ検索状態を更新
        if (this.enabledSearchReplace && this.debouncedUpdateSearchState) {
          this.debouncedUpdateSearchState();
        }
      },
      immediate: true,
    },
    enabledSearch(value: boolean) {
      if (!value) return;
      const { searchTargetIndex, searchTextIndexList, scrollToTargetText, focusInputSearch } = this;
      const { from } = searchTextIndexList[searchTargetIndex];
      scrollToTargetText(from);
      focusInputSearch();
      this.$store.dispatch("manuscriptModule/toggleEnabledSearch");
    },
    searchTargetIndex(value: number) {
      const { enabledSearchReplace, searchTextIndexList, scrollToTargetText, focusInputSearch } = this;
      if (!enabledSearchReplace || searchTextIndexList.length === 0) return;
      const { from } = searchTextIndexList[value];
      scrollToTargetText(from);
      focusInputSearch();
    },
    enabledReplaceSingle(value: boolean) {
      const {
        searchTextIndexList,
        searchTargetIndex,
        replaceText,
        scrollToTargetText,
        selectTargetText,
        executeReplaceText,
      } = this;

      if (value) {
        const { from, to } = searchTextIndexList[searchTargetIndex];
        executeReplaceText(from, to);
        this.$store.dispatch("manuscriptModule/toggleEnabledReplaceSingle");

        scrollToTargetText(from);
        selectTargetText(from, from + replaceText.length);
      }
    },
    async enabledReplaceAll(value: boolean) {
      const { searchText, replaceText } = this;

      if (value) {
        const replacedContent = this.content.replaceAll(searchText, replaceText);
        this.content = replacedContent; // WARNING: contentを直接変更しているため、undoが効かなくなる
        this.$store.dispatch("manuscriptModule/toggleEnabledReplaceAll");
      }
    },
    async restoredContent(value: string | null) {
      if (typeof value !== "string") return;

      // contentに反映する前に、現在の変更を保存する
      if (this.hasUnsavedChanges()) await this.save();

      this.content = value;
      this.$store.dispatch("manuscriptModule/initializeRestoredContent");
      this.updateCountsNow();
    },
    isManuscriptUnsavedAgentNavigation: {
      handler(newValue) {
        if (newValue) {
          this.save();
        }
      },
    },
  },

  computed: {
    dashLabel() {
      // dashInputState.type を参照して設定変更時に再描画する
      const { type } = dashInputState;
      return getDashPairText(type);
    },
    dashIcon() {
      // dashInputState.type を参照して設定変更時に自動で再評価する（Vue.observable）
      return dashInputState.type === "horizontalBar" ? IconHorizontalBar : IconDash;
    },
    novel() {
      const { novelId } = this;
      const { getters } = this.$store;
      const novel = getters["novelModule/novel"](novelId) as Novel | undefined;
      return novel;
    },
    manuscript() {
      const { getters } = this.$store;
      return getters["manuscriptModule/manuscript"](this.manuscriptKey);
    },
    contentLoadState() {
      return this.$store.getters["manuscriptModule/contentLoadState"];
    },
    bulkReplaceEvent() {
      return this.$store.getters["properNounModule/bulkReplaceEvent"];
    },
    changed() {
      return this.preTitle !== this.title || this.preContent !== this.content || !!this._typingDirty;
    },
    exceeded() {
      return this.syncContentLength() > contentMaxLength;
    },
    onlyCharacterWordCount() {
      return countOnlyCharacterWordCount(this.content);
    },
    isShowPreview() {
      const { $store, isFree } = this;
      const { getters } = $store;

      // 縦書きモードと縦書きプレビューは共存しない。両方 ON の場合は縦書きモードが勝ち、
      // 設定が ON でも縦書きプレビューは表示しない
      if (this.isVerticalWritingMode) {
        return false;
      }

      const isShowPreview = getters["manuscriptModule/isShowPreviewOnManuscriptEditor"];

      return !isFree && isShowPreview;
    },
    isAssistPanelVisible() {
      return this.$store.getters["assistNavigationModule/isVisible"];
    },
    isVerticalWritingMode() {
      // 脚本モードは専用の縦書きモード設定を参照する
      return this.isScriptMode
        ? this.$store.getters["manuscriptModule/isScriptVerticalWritingMode"]
        : this.$store.getters["manuscriptModule/isVerticalWritingMode"];
    },
    isScriptMode() {
      return this.$store.getters["novelModule/isScriptMode"](this.novelId);
    },

    isMobile() {
      // SCSS の @include mq(sp) と一致する判定
      // windowWidthをリアクティブな値として使用することで、リサイズに対応
      return this.windowWidth <= MOBILE_BREAKPOINT;
    },
    manuscriptList() {
      const key = this.manuscriptKey;
      const { title, content } = this;
      // 脚本モードの場合、縦書きプレビュー用にリフロー変換する（ガードスペース不要）。
      // ManuscriptPreview 側で breakVerticalLines が原稿用紙設定の wordCountOnVertical で
      // 再度行分割するため、ここでも同じ文字数で折り返しておく（二重折返しによる崩れ防止）。
      // 縦書き編集中は content が既に固定 20 字でリフロー済みのため、一度論理テキストに
      // 戻してから設定字数で再リフローする。ソフト改行マーカーも breakVerticalLines で
      // 1文字としてカウントされ、マーカーのみの余分な空行が生まれるため除去する
      let previewContent = content;
      if (this.isScriptMode) {
        const { wordCountOnVertical } = this.manuscriptSetting as ManuscriptSetting;
        // content が実際に縦書きリフロー済みかどうかは scriptTextFormat の判定基準
        // （isVerticalWritingMode && !isMobile）と揃える。モバイルでは縦書き表示中でも
        // content は論理テキストのまま保たれるため、ここで誤って unreflow すると壊れる
        const isVerticalDisplay = this.isVerticalWritingMode && !this.isMobile;
        const logicalContent = isVerticalDisplay ? unreflowVerticalToLogical(content) : content;
        previewContent = stripSoftBreakMarker(reflowLogicalToVertical(logicalContent, wordCountOnVertical, false));
      }
      const manuscriptObject: Manuscript = {
        key,
        title,
        content: previewContent,
      };
      return [manuscriptObject];
    },
    theme() {
      const storeTheme = this.$store.getters["manuscriptModule/theme"];
      const effectiveAppTheme = this.$store.getters["generalModule/effectiveTheme"] as "light" | "dark";
      return getEffectiveEditorTheme(storeTheme, effectiveAppTheme);
    },
    countType() {
      return this.$store.getters["manuscriptModule/countType"];
    },
    countTypeName() {
      const countTypeValue = this.countType;
      const countType = (this.countTypeList as Item[]).find((countType) => countType.value === countTypeValue);
      const countTypeName = countType ? countType.name : "";

      // 脚本モードでも小説と同様に原稿用紙設定（縦字数×行数）をそのまま表示する。
      // ページ換算（countScriptPages）もこの設定値で計算されるため表示と一致する。
      const { wordCountOnVertical, lineCountPerPage } = this.manuscriptSetting as ManuscriptSetting;

      return this.$t("manuscriptPage.manuscriptEditor.countFormat", {
        countTypeName,
        vertical: wordCountOnVertical,
        horizontal: lineCountPerPage,
      }) as string;
    },
    wordCountInfo() {
      const { displayedContentLength, displayedPageCount } = this;

      return this.$t("manuscriptPage.manuscriptEditor.wordCountInfo", {
        count: displayedContentLength,
        pages: displayedPageCount,
      }) as string;
    },
    plan() {
      return this.$store.getters["billingModule/plan"] as Plan;
    },
    isFree() {
      return this.plan === Plan.free;
    },
    manuscriptSetting() {
      return this.$store.getters["novelModule/manuscriptSetting"](this.novelId) as ManuscriptSetting;
    },
    saveStatus() {
      const { isSaving, saved } = this;

      if (isSaving) return "saving";
      if (saved) return "saved";
      return null;
    },
    lastModified() {
      return this.manuscript && this.manuscript.lastModified ? `最終保存：${this.manuscript.lastModified}` : "";
    },
    autoSave() {
      return this.$store.getters["manuscriptModule/autoSave"];
    },
    textareaElement() {
      return document.getElementById("textarea") as HTMLTextAreaElement;
    },
    enabledSearchReplace() {
      return this.$store.getters["manuscriptModule/enabledSearchReplace"];
    },
    enabledReplace() {
      return this.$store.getters["manuscriptModule/enabledReplace"];
    },
    searchText() {
      return this.$store.getters["manuscriptModule/searchText"];
    },
    replaceText() {
      return this.$store.getters["manuscriptModule/replaceText"];
    },
    enabledSearch() {
      return this.$store.getters["manuscriptModule/enabledSearch"];
    },
    enabledReplaceSingle() {
      return this.$store.getters["manuscriptModule/enabledReplaceSingle"];
    },
    enabledReplaceAll() {
      return this.$store.getters["manuscriptModule/enabledReplaceAll"];
    },
    searchTargetIndex() {
      return this.$store.getters["manuscriptModule/searchTargetIndex"];
    },
    searchTotalCount() {
      return this.$store.getters["manuscriptModule/searchTotalCount"];
    },
    searchTextIndexList() {
      return this.$store.getters["manuscriptModule/searchTextIndexList"];
    },
    restoredContent() {
      return this.$store.getters["manuscriptModule/restoredContent"];
    },

    // NolaNovel

    novelIdFromNolaNovel() {
      const { novel } = this;

      if (!novel) {
        return undefined;
      }

      const { associatedData } = novel as Novel;

      if (!associatedData) {
        return undefined;
      }

      const { nolaNovel } = associatedData;

      if (!nolaNovel) {
        return undefined;
      }

      const { id } = nolaNovel;

      if (!id) {
        return undefined;
      }

      return id;
    },
    episodeIdFromNolaNovel() {
      const { manuscript } = this;
      if (!manuscript) {
        return undefined;
      }

      const { associatedData } = manuscript as Manuscript;

      if (!associatedData) {
        return undefined;
      }

      const { nolaNovel } = associatedData;

      if (!nolaNovel) {
        return undefined;
      }

      const { id } = nolaNovel;

      if (!id) {
        return undefined;
      }

      return id;
    },
    isContentLengthOver50000() {
      return this.cachedTotalNovelContentLength >= CONTENT_LENGTH_OVER_50000;
    },
    isAgentPopupDisplayedForNovel() {
      const { $store, novelId } = this;
      return $store.getters["generalModule/isAgentPopupDisplayedForNovel"](novelId);
    },
    isManuscriptUnsavedAgentNavigation() {
      return this.agentStore.isManuscriptUnsavedAgentNavigation;
    },
  },

  methods: {
    // 脚本モードの表示テキスト形式（論理/縦書き）を現在のモードに同期する。
    // isScriptMode / isVerticalWritingMode の両 watcher から呼ばれるが、
    // scriptTextFormat で現在形式を管理し、目的形式と一致する場合は変換しない。
    // これにより両 watcher が同一 flush で発火してもリフロー/アンリフローは1回だけ適用される
    // （reflow は冪等でなく、二重適用するとソフト改行マーカーのみの余分な空行が混入するため）。
    // preContent（未保存判定の基準値）にも同じ変換をかけ、未保存の変更状態を維持する。
    syncScriptTextFormat() {
      if (this.isMobile) return;
      const next: "logical" | "vertical" = this.isScriptMode && this.isVerticalWritingMode ? "vertical" : "logical";
      if (this.scriptTextFormat === next) return;
      const convert = next === "vertical" ? reflowLogicalToVertical : unreflowVerticalToLogical;
      this.content = convert(this.content);
      this.preContent = convert(this.preContent);
      this.scriptTextFormat = next;
    },
    // initialize

    async initialize() {
      const { manuscript } = this;
      const { title, content } = manuscript;
      this.title = title || "";

      // 本文取得ステータスが PRESENT 以外の場合、原稿単位で通知する（MISSING_S3/CORRUPT_ZIPは区別せず統一文言）
      if (manuscript.contentStatus && manuscript.contentStatus !== "PRESENT") {
        this.$notify({
          title: this.$t("manuscriptPage.manuscriptEditor.contentUnavailable") as string,
          type: "error",
          duration: -1,
        });
      }

      // S3 上の原稿は常に論理テキスト（横書き形式）で保存されている。
      // 脚本モード＋縦書き表示の場合は 20 字折り返しを適用して表示用に変換する。
      let displayContent = content || "";
      const isVerticalDisplay = this.isScriptMode && this.isVerticalWritingMode && !this.isMobile;
      if (isVerticalDisplay) {
        displayContent = reflowLogicalToVertical(displayContent);
      }
      this.content = displayContent;
      // 現在の content 形式を記録する（以降の縦横/脚本モード切替での二重変換防止に使う）
      this.scriptTextFormat = isVerticalDisplay ? "vertical" : "logical";
      this.preTitle = String(this.title);
      this.preContent = String(this.content);
      this.updateCountsNow();

      // 登場シーン一覧からの遷移時（query.cursor）、該当位置にカーソルを移動する
      this.$nextTick(() => this.moveCursorFromQuery());

      // set RecordWordCount
      this.$store.commit("manuscriptModule/setBaseWordCount", this.onlyCharacterWordCount);

      // 各ショートカットキーの有効化と解除関数の取得
      this.unbindShortcutKeys = handleShortcutKeys([
        { trigger: TriggerKey.Meta, keys: ["s"], callback: this.save },
        { trigger: TriggerKey.Ctrl, keys: ["s"], callback: this.save },
        { trigger: TriggerKey.Meta, keys: ["f"], callback: this.enableSearchReplace },
        { trigger: TriggerKey.Ctrl, keys: ["f"], callback: this.enableSearchReplace },
        { trigger: TriggerKey.Meta, keys: ["h"], callback: this.enableReplace },
        { trigger: TriggerKey.Ctrl, keys: ["h"], callback: this.enableReplace },
        { keys: ["Escape", "esc"], callback: this.disableSearchReplace },
      ]);

      // 他の原稿の文字数を一度だけ計算してキャッシュ（パフォーマンス最適化）
      this.otherManuscriptsBaseLength = this.computeOtherManuscriptsLength();

      // 作品の執筆文字数を計算してStoreにセット
      this.updateTotalNovelContentLength();

      await this.openHistoryFromQuery();

      // 検索状態更新のデバウンス関数を初期化
      this.debouncedUpdateSearchState = debounce(() => this.updateSearchState(), 150);

      try {
        const results = await new UserClient().checkConnectedOtherServices();
        const { nolaNovel } = results;
        if (nolaNovel) {
          this.hasNolaNovelAccount = nolaNovel.result ?? false;
        }
      } catch (e) {
        // 特にエラーでも何もしない
      }
    },

    async openHistoryFromQuery() {
      if (!this.autoOpenHistory || this.hasOpenedHistory) return;
      this.hasOpenedHistory = true;
      this.$emit("history-opened");
      await this.$nextTick();
      const header = this.$refs.header as { showVersions?: () => Promise<void> } | undefined;
      if (header?.showVersions) {
        await header.showVersions();
      }
    },

    /**
     * 現在編集中の原稿以外の全原稿の文字数を計算する
     * パフォーマンス最適化のため、初期化時に1回だけ呼び出してキャッシュする
     */
    computeOtherManuscriptsLength() {
      const manuscriptList = this.$store.getters["manuscriptModule/manuscriptList"];
      const currentManuscriptKey = this.manuscriptKey;

      let totalLength = 0;

      manuscriptList.forEach((manuscript: Manuscript) => {
        if (manuscript.key === currentManuscriptKey) {
          return;
        }

        totalLength += countOnlyCharacterWordCount(manuscript.content || "");
      });

      return totalLength;
    },

    // execute

    // save() の多重呼び出し（手動保存ボタン / Cmd+S / 自動保存デバウンス / restoredContent watch /
    // isManuscriptUnsavedAgentNavigation watch など）を直列化するラッパー。
    // 実行中の保存があれば完了を待ってから、その時点の最新 content で改めて保存を実行する。
    // これにより「保存Aの完了で isSaving が false に戻った時点でまだ保存Bが通信中」
    // といったスピナーの誤表示や、preContent が別リクエストの内容で確定してしまう
    // レースコンディションを防ぐ（直列実行のため常に1件ずつ処理される）。
    async save() {
      // 直前の呼び出しが積んだ savePromise を Promise チェーンとしてつなぐ。
      // 「savePromise が無ければ即実行、あれば待ってから実行」を毎回同期的に
      // 行うことで、3件以上の呼び出しが重なっても常に1件ずつ順番に実行される
      // （if で1回待つだけの実装だと、複数件が同じ savePromise を待った後
      // 揃って performSave() を呼んでしまい並行実行が起こり得るため不可）。
      const previousSave = this.savePromise;
      const promise = (async () => {
        // 前段の保存が reject しても、この呼び出しの performSave() は独立して実行する。
        // catch せず await すると、前段の失敗がそのままこのチェーンにも伝播し、
        // 後続の保存が丸ごとスキップされた上で呼び出し元（自動保存デバウンス等）に
        // unhandled rejection として伝わってしまう
        if (previousSave) await previousSave.catch(() => {});
        await this.performSave();
      })();

      this.savePromise = promise;
      try {
        await promise;
      } finally {
        if (this.savePromise === promise) this.savePromise = null;
      }
    },
    async performSave() {
      const { novelId, title, manuscriptKey } = this;

      this.flushLiveContent();
      this._typingDirty = false;

      // 保存対象として確定する、リクエスト送信時点の表示中本文のスナップショット。
      // 通信中（await 中）に this.content がさらに編集される可能性があるため、
      // callback 内で「保存済み」として preContent に確定する値は、実際に
      // サーバーへ送信した時点のこのスナップショットを使う（this.content を
      // 直接参照すると、通信中の追加編集まで保存済み扱いになってしまう）。
      const displayContent = this.content;

      // 脚本モード＋縦書き時はソフト改行を除去して論理テキストに戻してから保存する
      const content =
        this.isScriptMode && this.isVerticalWritingMode && !this.isMobile
          ? unreflowVerticalToLogical(displayContent)
          : displayContent;

      if (!this.hasUnsavedChanges()) return;

      if (this.syncContentLength() > contentMaxLength) {
        this.showErrorDialog(
          "本文の文字数が10万字を越えています。<br />10万字以内に編集して再度保存を実行してください。<br />※別の原稿にて続きをお書きください。"
        );

        // エラーの場合はプロモーション風バナー利用済みフラグ/isManuscriptUnsavedAgentNavigationフラグを初期化する
        this.$store.dispatch("generalModule/setUsedAgentFeaturePromoBanner", false);
        this.agentStore.setManuscriptUnsavedAgentNavigation(false);

        // 10万字エラーダイアログ表示フラグを設定
        this.$store.commit("manuscriptModule/setWordLimitErrorDialogShown", true);
        return;
      }

      const key = manuscriptKey;
      this.isSaving = true;
      try {
        await this.$store.dispatch("manuscriptModule/updateManuscript", {
          novelId,
          key,
          title,
          content,
          callback: async () => {
            // 永続化後の UI 更新で例外が出ても updateManuscript 全体を失敗にしない。
            // 失敗トーストは API / dispatch 失敗に限定する。
            try {
              this.preTitle = clone(title);
              this.preContent = clone(displayContent);
              // 通信中の追加入力は onTyping の _typingDirty や this.content の差で
              // this.changed が true のまま残り、次の自動保存/手動保存で拾われる。
              // ここでは _typingDirty を落とさない（textarea 未同期の入力を消さないため）。
              this.setModified(this.changed);
              this.syncChangedToChrome();

              this.saved = true;
              // 連続保存時に前回分のタイマーが後から発火して表示中のチェックマークを
              // 消してしまわないよう、既存タイマーをクリアしてから積み直す
              if (this.savedTimerId !== null) clearTimeout(this.savedTimerId);
              this.savedTimerId = setTimeout(() => {
                this.saved = false;
                this.savedTimerId = null;
              }, 2000);

              if (this.isScriptMode) {
                try {
                  this.nolaAnalytics.logButtonEvent(NolaItemId.ScriptModeManuscriptSaved, manuscriptKey);
                } catch {
                  // analytics エラーは保存後の処理に影響させない
                }
              }

              // 保存成功時に作品全体の文字数を再計算
              this.updateTotalNovelContentLength();

              // 作品文字数が5万文字を超えた場合かつ、これまでエージェント機能ポップアップを
              // 表示していない作品の場合、ポップアップを表示する
              if (this.isContentLengthOver50000 && !this.isAgentPopupDisplayedForNovel) {
                // 自動保存が有効な場合、即時にポップアップを表示せず、
                // Storeに表示フラグをセットし、ホーム画面に遷移したときにポップアップを表示する
                if (this.autoSave) {
                  this.$store.commit("generalModule/setAgentPopupDisplayFlagNovelId", novelId);
                } else {
                  try {
                    const agentPromotionDialog = new Dialog(AgentPromotionDialog);
                    agentPromotionDialog.show({ novelTitle: this.novel!.title });
                    await this.$store.dispatch("generalModule/addDisplayedAgentPopupForNovel", novelId);
                  } catch {
                    // エージェント機能プロモーションポップアップ表示に関するエラーは、
                    // 既に永続化済みの原稿保存自体の成否（「保存に失敗しました」表示）には影響させない
                  }
                }
              }
            } catch (error) {
              console.error("Failed to run manuscript save callback:", error);
            }
          },
        });
      } catch {
        this.$notify({
          title: this.$t("manuscriptPage.manuscriptEditor.saveFailed") as string,
          type: "error",
          duration: 2000,
        });
        // 10万字超過エラーと同様、失敗時はエージェント遷移フラグを初期化する。
        // リセットしないと、このフラグは次に成功するまでPiniaストアに残り続けるため、
        // 今回の保存とは無関係な後続の保存成功時に意図しないエージェント画面遷移が起きる
        this.$store.dispatch("generalModule/setUsedAgentFeaturePromoBanner", false);
        this.agentStore.setManuscriptUnsavedAgentNavigation(false);
        // 保存に失敗した場合、以降の「保存成功」を前提とした処理
        // （文字数カウント加算・未保存フラグクリアしてのエージェント画面遷移）は実行しない
        return;
      } finally {
        this.isSaving = false;
      }

      this.$store.dispatch("manuscriptModule/incrementWritingCount", {
        novelId,
        value: this.onlyCharacterWordCount,
      });

      // 原稿未保存状態でエージェント機能プロモーションダイアログのボタンが
      // 押下された場合、エージェントイントロ画面へ遷移する。
      // ただし this.changed（通信中の追加編集を含む最新の未保存判定）が true の間は
      // まだ保存し切れていない内容が残っている可能性があるため遷移しない。
      // save() は直列化されているため、Promiseチェーン上の後続の保存（キュー済みの
      // 呼び出し）が完了した時点で改めてこの条件をチェックし、その時点で changed が
      // false になっていれば遷移する。this.setModified() の呼び出しは不要
      // （callback内の this.setModified(this.changed) が既に正しい状態に更新済み）。
      if (this.isManuscriptUnsavedAgentNavigation && !this.changed) {
        this.$router.push({
          name: "agent",
        });
      }
    },
    /**
     * 作品全体の文字数を計算し、キャッシュとStoreを更新する
     * 保存時と初期化時にのみ呼び出す
     * パフォーマンス最適化: 他の原稿の文字数は初期化時に一度だけ計算してキャッシュ
     * 現在開いている原稿の文字数のみをリアルタイムで計算
     */
    updateTotalNovelContentLength(): void {
      // 現在開いている原稿の文字数から空白や改行を除いた文字数を計算
      // 注意: 元の実装と同様に、現在の原稿はルビを除外せずにカウント
      const currentContentLength = this.content.replace(/⁠/g, "").replace(/\s+|\n/g, "").length;
      const totalContentLength = this.otherManuscriptsBaseLength + currentContentLength;

      // キャッシュを更新
      this.cachedTotalNovelContentLength = totalContentLength;

      // Storeを更新
      this.$store.commit("novelModule/setNovelContentLength", totalContentLength);
    },
    async goToMultiEditor() {
      const { novelId, manuscriptKey } = this;
      if (this.isFree) {
        // NOTE: 機能紹介GIFを修正するため一時的にコメントアウト
        // const premiumFeatureDialog = new Dialog(PremiumFeatureDialog);
        // const data: PremiumFeatureDialogProps = {
        //   title: "マルチ執筆モードはプレミアム機能です",
        //   text: "Nolaプレミアムにご登録いただくと【全画面表示】で【資料を見ながら】執筆が可能な『マルチ執筆モード』をご利用いただけます。執筆がリアルタイムに反映される縦書きプレビューも同時にご利用いただけますので、お手すきの際にぜひご利用いただけますと幸いです。",
        //   image: (await downloadPublicImage("shared/images/gif/manuscript_multi_editor.gif")) as string,
        // };
        // premiumFeatureDialog.show(data);

        this.$router.push({
          name: "subscriptionAnnounce",
          query: { from: "multiEditor" },
        });

        return;
      }

      this.$router.push({
        name: "multiEditor",
        params: {
          novelId,
          manuscriptKey,
        },
      });
    },
    async navigateToNolaNovelEditPage(id: string) {
      const url = process.env.VUE_APP_NOLANOVEL_WEB;

      if (!url) {
        throw new Error("NolaノベルのWebサイトURLが環境変数に含まれていません。");
      }

      const { novelId, manuscript } = this;
      const service = new NolaNovelUrlGenerator(process.env.VUE_APP_NOLANOVEL_WEB!);
      const updateEpisodeUrl = await service.genUpdateEpisodeUrl(id, novelId, manuscript.key!);
      window.open(
        createUrlWithUtmParams(updateEpisodeUrl, NolaPageName.Editor, NolaItemId.ConfirmOverwriteEpisodeButton),
        process.env.VUE_APP_NOLANOVEL_NAME
      );
    },
    executeReplaceText(from, to) {
      const { textareaElement, replaceText } = this;
      // フォーカスを先に設定してから選択範囲を設定する
      textareaElement.focus();
      textareaElement.selectionStart = from;
      textareaElement.selectionEnd = to;
      document.execCommand("insertText", false, replaceText);
    },
    initializeSearchState() {
      this.disableSearchReplace();
      this.$store.dispatch("manuscriptModule/updateSearchText", "");
      this.$store.dispatch("manuscriptModule/updateReplaceText", "");
    },
    updateSearchState() {
      const { searchText, searchTargetIndex } = this;
      const searchTextIndexList: SearchTextIndex[] = [];

      if (searchText) {
        // エスケープ前に1文字ずつ分割してからパターンを組み立てる
        // （エスケープ後に split すると \( が \ と ( に分かれて壊れるため）
        const escapeRegExpChar = (c: string) => c.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

        // ルビなしとルビありの両方に対応する正規表現パターン
        // ルビ付き文字の部分とプレーンテキストの部分を分離して検出
        const rubyPattern = [...searchText]
          .map((char: string) => {
            const escaped = escapeRegExpChar(char);
            // 「|」「｜」はルビの親文字記号と同じ文字なので、
            // [｜|]? プレフィックスを付けると余分に消費してしまう
            if (char === "|" || char === "｜") {
              return `${escaped}(?:《[^》]*》)?`;
            }
            return `[｜|]?${escaped}(?:《[^》]*》)?`;
          })
          .join("");

        const searchRegExp = new RegExp(rubyPattern, "gu");

        let match: RegExpExecArray | null;
        // eslint-disable-next-line no-cond-assign
        while ((match = searchRegExp.exec(this.content)) !== null) {
          // 空文字マッチによる無限ループを防止
          if (match[0].length === 0) {
            searchRegExp.lastIndex += 1;
          } else {
            // 検索結果の開始インデックス
            const from = match.index;
            // 検索結果の終了インデックス
            const to = match.index + match[0].length;
            // 検索結果をリストに追加
            searchTextIndexList.push({ from, to });
          }
        }
      }

      this.$store.dispatch("manuscriptModule/updateSearchTextIndexList", searchTextIndexList);
      // 検索結果が変わった場合のみインデックスをリセット
      if (searchTargetIndex >= searchTextIndexList.length) {
        this.$store.dispatch("manuscriptModule/updateSearchTargetIndex", 0);
      }
      this.$store.dispatch("manuscriptModule/updateSearchTotalCount", searchTextIndexList.length);
    },

    // event handle

    async handleBackAction() {
      const { novelId } = this;
      if (this.hasUnsavedChanges()) {
        const yes = await showOkCancelDialog({
          title: "確認",
          content: "保存されていない変更があります。<br />変更を破棄して戻りますか？",
          okButton: "破棄する",
          cancelButton: "キャンセル",
        });
        if (!yes) {
          return;
        }
      }

      this.$router.push({ name: "editor", params: { novelId } });
    },
    onExportButtonClick(isPost) {
      const { hasNolaNovelAccount } = this;
      if (!hasNolaNovelAccount) {
        const introduction = new Dialog(NolaNovelIntroduction);
        introduction.show();
        return;
      }

      if (this.hasUnsavedChanges()) {
        this.showUnsavedConfirm();
        return;
      }

      const { episodeIdFromNolaNovel, showNolaNovelExportConfirm } = this;

      if (episodeIdFromNolaNovel) {
        showNolaNovelExportConfirm(episodeIdFromNolaNovel);
        return;
      }

      const { showNolaNovelListDialog } = this;
      showNolaNovelListDialog(isPost);
    },
    async onClickPreview() {
      if (this.isFree) {
        // NOTE: 機能紹介GIFを修正するため一時的にコメントアウト
        // const premiumFeatureDialog = new Dialog(PremiumFeatureDialog);
        // const data: PremiumFeatureDialogProps = {
        //   title: "執筆時の縦書きプレビューはプレミアム機能です",
        //   text: "Nolaプレミアムにご登録いただくと、原稿の執筆時にリアルタイムで反映される『縦書きプレビュー機能』をご利用いただけます。一行辺りの文字数やページ辺りの行数も設定可能ですので、お手すきの際にぜひご活用いただだけたら幸いです。",
        //   image: (await downloadPublicImage("shared/images/gif/manuscript_vertical_writing.gif")) as string,
        // };
        // premiumFeatureDialog.show(data);

        this.$router.push({
          name: "subscriptionAnnounce",
          query: { from: "verticalWriting" },
        });

        return;
      }
      await this.$store.dispatch("manuscriptModule/switchPreviewOnManuscriptEditor");

      if (this.isScriptMode && this.isShowPreview) {
        try {
          this.nolaAnalytics.logButtonEvent(NolaItemId.ScriptModePreviewOpened, this.manuscriptKey);
        } catch {
          // analytics エラーはプレビュー表示に影響させない
        }
      }
    },
    async onChangeTitle(args) {
      const { value, isComposing } = args;

      this.title = value;
      this.setModified(this.hasUnsavedChanges());
      this.syncChangedToChrome();

      if (this.hasUnsavedChanges() && this.autoSave && !isComposing) {
        this.scheduleLocalAutosave();
      }
    },
    syncChangedToChrome() {
      const changed = this.hasUnsavedChanges();
      const header = this.$refs.header as { setChanged?: (value: boolean) => void } | undefined;
      header?.setChanged?.(changed);
      const spSave = this.$refs.spSave as HTMLElement | undefined;
      if (spSave) {
        // CSS は属性の有無で無効表示を判定するため、"false" を書かずに属性自体を外す
        if (changed) {
          spSave.removeAttribute("data-disabled");
        } else {
          spSave.setAttribute("data-disabled", "true");
        }
      }
    },
    hasUnsavedChanges() {
      return this.preTitle !== this.title || this.preContent !== this.content || !!this._typingDirty;
    },
    onTyping(args) {
      const isComposing = !!(args && args.isComposing);
      if (this.isComposingText !== isComposing) {
        this.isComposingText = isComposing;
      }
      if (!this._typingDirty) {
        this._typingDirty = true;
        this.setModified(true);
        this.syncChangedToChrome();
      }
      if (this.autoSave && !isComposing) {
        this.scheduleLocalAutosave();
      }
    },
    async onChangeContent(args) {
      if (!args) return;
      const { value, isComposing } = args;

      this._typingDirty = false;
      if (this.isComposingText !== !!isComposing) {
        this.isComposingText = !!isComposing;
      }
      this.content = value;
      this.setModified(this.hasUnsavedChanges());
      this.syncChangedToChrome();
      this.checkUndoRedo();
    },
    onStartComposition() {
      this.isComposingText = true;
      this.clearLocalAutosave();
    },
    onEndComposition() {
      this.isComposingText = false;
      this.updateCountsNow();
      if (this.hasUnsavedChanges() && this.autoSave) {
        this.scheduleLocalAutosave();
      }
    },
    onSelectCountType(countTypeValue) {
      this.$store.dispatch("manuscriptModule/selectCountType", countTypeValue);
    },
    getCountContext() {
      const { wordCountOnVertical, lineCountPerPage } = this.manuscriptSetting as ManuscriptSetting;
      return {
        isScriptMode: this.isScriptMode,
        isVerticalWritingMode: this.isVerticalWritingMode,
        wordCountOnVertical,
        lineCountPerPage,
      };
    },
    syncContentLength() {
      return countContentLength(this.getLiveContent(), this.countType, this.getCountContext());
    },
    updateCountsNow() {
      if (this.debouncedUpdateCounts) {
        this.debouncedUpdateCounts.cancel();
      }
      const counts = countManuscriptDisplay(this.getLiveContent(), this.countType, this.getCountContext());
      this.displayedContentLength = counts.contentLength;
      this.displayedPageCount = counts.pageCount;
    },
    getLiveContent() {
      const editor = this.$refs.editor as { getValue?: () => string } | undefined;
      if (editor && typeof editor.getValue === "function") {
        return editor.getValue();
      }
      return this.content;
    },
    flushLiveContent() {
      const editor = this.$refs.editor as { flushParentSync?: () => void } | undefined;
      if (editor && typeof editor.flushParentSync === "function") {
        editor.flushParentSync();
      }
    },
    scheduleLocalAutosave() {
      this.clearLocalAutosave();
      (this as { _localAutosaveTimer?: ReturnType<typeof setTimeout> | null })._localAutosaveTimer = setTimeout(() => {
        (this as { _localAutosaveTimer?: ReturnType<typeof setTimeout> | null })._localAutosaveTimer = null;
        this.save();
      }, 3000);
    },
    clearLocalAutosave() {
      const vm = this as { _localAutosaveTimer?: ReturnType<typeof setTimeout> | null };
      if (vm._localAutosaveTimer) {
        clearTimeout(vm._localAutosaveTimer);
        vm._localAutosaveTimer = null;
      }
    },
    enableSearchReplace() {
      const { $store, enabledSearchReplace } = this;

      if (!enabledSearchReplace) $store.dispatch("manuscriptModule/toggleEnabledSearchReplace");

      const selectedText = window.getSelection()!.toString();
      if (selectedText) $store.dispatch("manuscriptModule/updateSearchText", selectedText);

      // 検索ダイアログにフォーカスを設定
      this.$nextTick(() => {
        const searchInput = document.getElementById("inputSearch") as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
        }
      });

      // 検索UIが開かれた時に検索状態を即座に更新
      this.updateSearchState();
    },
    enableReplace() {
      const { $store, enabledReplace, enableSearchReplace } = this;
      enableSearchReplace();
      if (!enabledReplace) $store.dispatch("manuscriptModule/toggleEnabledReplace");
    },
    disableSearchReplace() {
      if (this.enabledSearchReplace) this.$store.dispatch("manuscriptModule/toggleEnabledSearchReplace");
    },
    scrollToTargetText(to) {
      // NOTE: 検索対象文字がtextareaの表示外にあるとき、検索対象文字までスクロールさせる
      const { textareaElement } = this;
      textareaElement.selectionStart = to;
      textareaElement.selectionEnd = to;
      textareaElement.blur();
      textareaElement.focus();
    },
    moveCursorFromQuery() {
      // 名称管理の登場シーン一覧から query.cursor（絶対位置）付きで遷移してきた場合、
      // 該当する固有名詞の先頭にキャレットを置く（選択状態にはしない）
      const { cursor } = this.$route.query;
      if (cursor === undefined || cursor === null) return;

      const rawCursor = Array.isArray(cursor) ? cursor[0] : cursor;
      const position = Number(rawCursor);
      if (!Number.isFinite(position) || position < 0) return;

      const { textareaElement } = this;
      if (!textareaElement) return;

      const caret = Math.min(position, textareaElement.value.length);

      // モーダルのクローズに伴うフォーカス移動が落ち着いた後に実行しないと
      // （同一原稿への遷移時に）フォーカスが奪われてカーソルが消えるため、
      // setTimeout で次のタスクに遅延させてからキャレットを確定する
      window.setTimeout(() => {
        textareaElement.selectionStart = caret;
        textareaElement.selectionEnd = caret;
        // 表示領域外のときにスクロールさせるため blur → focus で反映する
        textareaElement.blur();
        textareaElement.focus();
        // 一度カーソルを移動したら cursor クエリは不要。残すとリロード時に再移動するため URL から除去する
        // （他のクエリは保持。cursor 削除で再発火する watch は cursor 未定義のため早期 return する）
        const query = { ...this.$route.query };
        if (query.cursor !== undefined) {
          delete query.cursor;
          this.$router.replace({ path: this.$route.path, query }).catch(() => {
            // 同一URLへの置換（NavigationDuplicated）等は無視
          });
        }
      }, 0);
    },
    selectTargetText(from, to) {
      const { textareaElement } = this;
      textareaElement.selectionStart = from;
      textareaElement.selectionEnd = to;
      textareaElement.focus();
    },
    focusInputSearch() {
      const inputSearchElement = document.getElementById("inputSearch") as HTMLInputElement | null;
      inputSearchElement?.focus();
    },

    // view

    showUnsavedConfirm() {
      const completeDialog = new Dialog(CupertinoAlertDialog);
      const data: CupertinoAlertDialogProps = {
        title: "未保存の内容があります",
        content: "未保存のデータがあります。保存を実行してから投稿することが可能です。",
        action: "閉じる",
      };
      completeDialog.show(data);
    },
    async showMenu() {
      openManuscriptEditorMenuDialog();
    },
    showErrorDialog(message: string) {
      const confirmDialog = new Dialog(DialogVue);
      const options = {
        title: "エラー",
        content: message,
      };

      confirmDialog.show(options).then(() => {
        // 10万字エラーダイアログが閉じられたタイミングで
        // プロモーションバナー再表示のため、フラグをリセット
        this.$store.commit("manuscriptModule/setWordLimitErrorDialogShown", false);
      });
    },

    async showNolaNovelExportConfirm(id) {
      const confirmDialog = new Dialog(NolaNovelUpdateConfirm);
      const data: NolaNovelUpdateConfirmProps = {
        id,
        content: `この原稿を、Nolaノベル上に投稿された連携済みエピソードに上書きしてよろしいですか？`,
      };
      const result = await confirmDialog.show(data);
      const { navigateToNolaNovelEditPage } = this;
      if (result) {
        navigateToNolaNovelEditPage(id);
      }
    },
    async showNolaNovelListDialog(isPost: boolean) {
      const { novelId, manuscriptKey, novelIdFromNolaNovel } = this;
      const novelListDialog = new Dialog(EpisodeListDialog);
      const data: EpisodeListDialogProps = {
        novelId,
        manuscriptId: manuscriptKey,
        isPost,
        novelIdFromNolaNovel,
      };

      const result = await novelListDialog.show(data);
      if (result) {
        const completeDialog = new Dialog(CupertinoAlertDialog);
        const data: CupertinoAlertDialogProps = {
          title: "連携が完了しました",
          content: "今後はこの原稿をNolaからNolaノベルに投稿する際は、自動でエピソードが選択されます。",
          action: "閉じる",
        };
        completeDialog.show(data);
      }

      const { initialize } = this;
      initialize();
    },
    async showNolaNovelDelinkConfirmDialog(id) {
      const { novelId, manuscriptKey } = this;
      const deleteDialog = new Dialog(EpisodeDelinkConfirm);
      const data: EpisodeDelinkConfirmProps = {
        novelId,
        manuscriptId: manuscriptKey,
        episodeId: id,
      };

      const result = await deleteDialog.show(data);
      if (result) {
        const completeDialog = new Dialog(CupertinoAlertDialog);
        const data: CupertinoAlertDialogProps = {
          title: "連携を解除しました",
          content:
            "再度連携を行う場合は、Nolaノベルアイコンまたはメニュー内の「Nolaノベルと連携する」を選択してください。",
          action: "閉じる",
        };
        completeDialog.show(data);
      }

      const { initialize } = this;
      initialize();
    },
    undo() {
      document.execCommand("undo", false, undefined);
    },
    redo() {
      document.execCommand("redo", false, undefined);
    },
    checkUndoRedo() {
      this.canUndo = document.queryCommandEnabled("undo");
      this.canRedo = document.queryCommandEnabled("redo");
    },
    brackets() {
      const { textareaElement } = this;
      textareaElement.focus();
      const { selectionStart } = textareaElement;
      const selectedText = window.getSelection()!.toString();
      document.execCommand("insertText", false, `「${selectedText}」`);

      // NOTE: 文字選択をしていない場合、括弧の間にカーソルを移動
      if (!selectedText) {
        textareaElement.selectionStart = selectionStart + 1;
        textareaElement.selectionEnd = selectionStart + 1;
      }
      // カーソル調整後にリフローを確実に発火する（execCommand が input を発火しないブラウザ対策）
      this.dispatchInputForReflow();
    },
    ellipsis() {
      const { textareaElement } = this;
      textareaElement.focus();
      document.execCommand("insertText", false, "……");
      this.dispatchInputForReflow();
    },
    space() {
      const { textareaElement } = this;
      textareaElement.focus();
      document.execCommand("insertText", false, "　");
      this.dispatchInputForReflow();
    },
    dash() {
      const { textareaElement } = this;
      textareaElement.focus();
      document.execCommand("insertText", false, getDashPairText());
      this.dispatchInputForReflow();
    },
    async ruby() {
      const { textareaElement } = this;
      textareaElement.focus();
      const selectedText = window.getSelection()!.toString();

      const rubyDialog = new Dialog(RubyDialog);
      const data: RubyDialogProps = {
        selectedText,
        negative: "キャンセル",
        positive: "入力する",
      };
      const result = await rubyDialog.show(data);

      textareaElement.focus();

      if (!result) return;
      document.execCommand("insertText", false, result);
    },
    async emphasisMark() {
      const { textareaElement } = this;
      textareaElement.focus();
      const selectedText = window.getSelection()!.toString();

      const emphasisMarkDialog = new Dialog(EmphasisMarkDialog);
      const data: EmphasisMarkDialogProps = {
        selectedText,
        negative: "キャンセル",
        positive: "入力する",
      };
      const result = await emphasisMarkDialog.show(data);

      textareaElement.focus();

      if (!result) return;
      document.execCommand("insertText", false, result);
    },
    insertPillar() {
      const { textareaElement } = this;
      if (!textareaElement) return;
      insertPillarTo(textareaElement, this.dispatchInputForReflow);
    },
    insertFlashback() {
      const { textareaElement } = this;
      if (!textareaElement) return;
      insertFlashbackTo(textareaElement, this.dispatchInputForReflow);
    },
    insertTogaki() {
      const { textareaElement } = this;
      if (!textareaElement) return;
      insertTogakiTo(textareaElement, this.dispatchInputForReflow);
    },
    dispatchInputForReflow() {
      // execCommand("insertText") が一部ブラウザ（iOS Safari 等）で input イベントを
      // 発火しないことがあり、リフローと content 同期が起きない問題への対策。
      // input を発火するブラウザでは2回目の reflowScriptBlock は変化なしで即返るので安全。
      const textarea = this.textareaElement;
      if (!textarea) return;
      textarea.dispatchEvent(
        new InputEvent("input", {
          bubbles: true,
          cancelable: true,
          composed: true,
          inputType: "insertText",
          isComposing: false,
        })
      );
    },
    handleCommentIconClick() {
      this.$emit("commentIconClicked");
    },
    updateWindowWidth() {
      this.windowWidth = window.innerWidth;
    },
  },
});

interface Props {
  novelId: string;
  manuscriptKey: string;
  autoOpenHistory?: boolean;
}

interface Data {
  title: string;
  preTitle: string;
  content: string;
  preContent: string;
  scriptTextFormat: "logical" | "vertical";
  saved: boolean;
  isSaving: boolean;
  savePromise: Promise<void> | null;
  savedTimerId: ReturnType<typeof setTimeout> | null;
  countTypeList: Item[];
  hasNolaNovelAccount: boolean;
  defaultColor: string;
  activeColor: string;
  canUndo: boolean;
  canRedo: boolean;
  unbindShortcutKeys: (() => void) | null;
  agentStore: AgentStore;
  windowWidth: number;
  debouncedUpdateWindowWidth: ReturnType<typeof debounce> | null;
  cachedTotalNovelContentLength: number;
  otherManuscriptsBaseLength: number;
  debouncedUpdateSearchState: ReturnType<typeof debounce> | null;
  displayedContentLength: number;
  displayedPageCount: number;
  isComposingText: boolean;
  _typingDirty?: boolean;
  debouncedUpdateCounts: ReturnType<typeof debounce> | null;
  nolaAnalytics: NolaAnalytics;
  hasOpenedHistory: boolean;
}

interface Methods {
  syncScriptTextFormat(): void;
  initialize(): void;
  openHistoryFromQuery(): Promise<void>;
  computeOtherManuscriptsLength(): number;

  save(): Promise<void>;
  performSave(): Promise<void>;
  updateTotalNovelContentLength(): void;
  handleBackAction(): Promise<void>;
  goToMultiEditor(): Promise<void>;
  navigateToNolaNovelEditPage(id: string): void;
  executeReplaceText(from: number, to: number): void;
  initializeSearchState(): void;
  updateSearchState(): void;

  onClickPreview(): void;
  onSelectCountType(countTypeValue: any): void;
  getCountContext(): {
    isScriptMode: boolean;
    isVerticalWritingMode: boolean;
    wordCountOnVertical: number;
    lineCountPerPage: number;
  };
  syncContentLength(): number;
  updateCountsNow(): void;
  getLiveContent(): string;
  flushLiveContent(): void;
  scheduleLocalAutosave(): void;
  clearLocalAutosave(): void;
  onChangeTitle(args: IEditorEmitArgs): Promise<void>;
  syncChangedToChrome(): void;
  hasUnsavedChanges(): boolean;
  onTyping(args: { isComposing?: boolean }): void;
  onChangeContent(args: IEditorEmitArgs): Promise<void>;
  onStartComposition(): void;
  onEndComposition(): void;
  onExportButtonClick(isPost: boolean): void;
  enableSearchReplace(): void;
  enableReplace(): void;
  disableSearchReplace(): void;
  scrollToTargetText(to: number): void;
  selectTargetText(from: number, to: number): void;
  moveCursorFromQuery(): void;
  focusInputSearch(): void;

  showUnsavedConfirm(): void;
  showMenu(): Promise<void>;
  showErrorDialog(message: string): void;
  showNolaNovelExportConfirm(id: string): void;
  showNolaNovelListDialog(isPost: boolean): void;
  showNolaNovelDelinkConfirmDialog(id: string): void;
  undo(): void;
  redo(): void;
  checkUndoRedo(): void;
  brackets(): void;
  ellipsis(): void;
  space(): void;
  dash(): void;
  ruby(): void;
  emphasisMark(): void;
  insertPillar(): void;
  insertFlashback(): void;
  insertTogaki(): void;
  dispatchInputForReflow(): void;
  handleCommentIconClick(): void;
  updateWindowWidth(): void;
}

interface Computed {
  dashLabel: string;
  dashIcon: typeof IconDash;
  novel?: Novel;
  manuscript: Manuscript;
  contentLoadState: "idle" | "loading" | "loaded";
  bulkReplaceEvent: BulkReplaceEvent | null;
  changed: boolean;
  exceeded: boolean;
  onlyCharacterWordCount: number;
  isShowPreview: boolean;
  isAssistPanelVisible: boolean;
  isVerticalWritingMode: boolean;
  isScriptMode: boolean;
  isMobile: boolean;
  manuscriptList: Manuscript[];
  theme: string;
  countType: string;
  countTypeName: string;
  wordCountInfo: string;
  plan: Plan;
  isFree: boolean;
  manuscriptSetting: ManuscriptSetting;
  saveStatus: SaveStatus;
  lastModified: string;
  autoSave: boolean;
  textareaElement: HTMLTextAreaElement;
  enabledSearchReplace: boolean;
  enabledReplace: boolean;
  searchText: string;
  replaceText: string;
  enabledSearch: boolean;
  enabledReplaceSingle: boolean;
  enabledReplaceAll: boolean;
  searchTargetIndex: number;
  searchTotalCount: number;
  searchTextIndexList: SearchTextIndex[];
  restoredContent: string | null;
  isContentLengthOver50000: boolean;
  isAgentPopupDisplayedForNovel: boolean;
  isManuscriptUnsavedAgentNavigation: boolean;

  // NolaNovel
  novelIdFromNolaNovel?: string;
  episodeIdFromNolaNovel?: string;
}
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";
/* パソコンで見たときは"pc"のclassがついた画像が表示される */
.sp {
  display: none !important;
}
/* スマートフォンで見たときは"sp"のclassがついた画像が表示される */
@media only screen and (max-width: 750px) {
  .pc {
    display: none !important;
  }
  .sp {
    display: block !important;
  }
}
/* 画面幅が狭いときに"md"のclassがついた要素が非表示になる */
@media only screen and (max-width: 1240px) {
  .md {
    display: none !important;
  }
}

.root {
  height: 100vh;
}

.manuscript-editor {
  display: grid;
  height: 100%;
  min-height: 0;
  grid-template-columns: 1fr;
  grid-template-rows: minmax(0, 1fr);

  @include mq(sp) {
    position: fixed;
    /*
     * iOS Safari でアドレスバーが縮小した際の空白を防ぐため、
     * inset: 0 を使用してビューポート全体をカバー
     */
    inset: 0;
    grid-template-rows: 39px 1fr;
    border-left: none;
    z-index: 200; /* Header.vue (z-index: 20) より高く、ダイアログ (z-index: 9000) より低い値 */
    margin: 0;
    /*
     * iOS Safari で position: fixed の z-index が正しく機能しない問題への対応
     * isolation: isolate で新しいスタッキングコンテキストを作成
     */
    isolation: isolate;
    /* 背後の SP 用下部ツールバー（SideMenu）が透けて見えないように背景を指定 */
    background-color: var(--color-background-normal);
    /*
     * 親要素のスクロールを無効化し、テキストエリアのみがスクロールするようにする
     * これにより二重スクロールを防止
     */
    overflow: hidden;
  }
}

.main-content {
  display: grid;
  height: 100%;
  min-height: 0;
  grid-template-columns: 1fr;
  /* auto 行だとパネルが内容高さになり、内側 overflow-y が効かず縦スクロール不能になる */
  grid-template-rows: minmax(0, 1fr);
  position: relative;

  &.with-assist {
    grid-template-columns: minmax(0, 1fr) auto;
  }

  .assist-panel {
    box-sizing: border-box;
    border-left: 1px solid var(--color-border);
    align-self: stretch;
    min-height: 0;
    overflow: hidden;
  }
}

.manuscript-container {
  display: grid;
  height: 100%;
  grid-template-rows: 55px calc(100vh - 105px) 50px;
  grid-template-columns: 1fr;

  @include mq(sp) {
    /* SP では .bottom.pc は非表示のため、3行目を確保しない */
    grid-template-rows: 55px 1fr;
    grid-auto-rows: 0; /* 3つ目の子 .bottom.pc が作る暗黙行の高さを 0 に */
  }
}

.editor-container {
  display: grid;
  height: 100%;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  position: relative;

  @include mq(sp) {
    height: 100%;
    min-height: 0;
  }

  &.isShowPreview {
    grid-template-columns: 1fr 1fr;
  }

  .preview {
    box-sizing: border-box;
    border-left: 1px solid var(--color-border);
  }

  .save-status-indicator {
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 1;
  }
}

.controller {
  width: 100%;
  height: 50px;
  padding: 12px 16px;

  background-color: var(--color-surface-muted);
  border-top: 1px solid var(--color-border);
  border-bottom: 1px solid var(--color-border);
}

/*文字数カウンターのレイアウト*/
.text-counter {
  font-size: larger;
  float: right;
  margin: 15px;
  color: rgb(90, 90, 90);
  @include mq(sp) {
    position: fixed;
    right: 10px;
    bottom: 15px;
    padding: 2px 6px;
    background-color: rgba(255, 255, 255, 0.8);
    border-radius: var(--radius-sm);
    margin: 0;
    font-size: var(--font-size-xs);
    z-index: 999;
  }

  &.error {
    color: var(--color-warning);
  }
}

/*保存ボタンのレイアウト*/
.save {
  position: absolute;
  height: 21px;
  right: 40px;
  top: 0px;
  padding: 9px;
  text-decoration: none;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-on-fixed-bg);
  background-color: var(--color-primary);
  line-height: 0;

  &[data-disabled] {
    color: rgba(255, 255, 255, 0.4);
    background-color: var(--color-inactive);
  }
}
.save img {
  height: 14px;
  margin-right: 5px;
}
.edit-manu {
  position: absolute;
  height: 21px;
  right: 0px;
  top: 0px;
  padding: 10px 6px;
  text-decoration: none;
  line-height: 0;
}
.edit-manu img {
  height: 14px;
  padding: 0 5px;
}

.button {
  position: relative;
  margin: 0;
  padding: 10px 30px;
  border-radius: var(--radius-xs);
  font-weight: var(--font-weight-bold);
  transition: all 0.3s;
  cursor: pointer;

  @include hover-overlay;

  &.primary {
    padding: 10px 50px;

    &:not(:disabled) {
      color: var(--color-text-on-fixed-bg);
      background-color: var(--color-primary);
      border-color: var(--color-primary);
    }

    &:disabled {
      background-color: var(--color-surface-base);

      &::after {
        display: none;
      }

      &:hover {
        cursor: auto;
      }
    }
  }

  &.default {
    color: var(--color-text-normal);
    background-color: var(--color-surface-base);
    border: none;
    border-radius: var(--radius-rounded);
  }
}

/*SP：一覧に戻るボタンのレイアウト*/
.back.sp {
  display: inline-block !important;
  padding: 10px;
  text-decoration: none;
  color: var(--color-text-on-fixed-bg);
  background-color: transparent;
  border: none;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
}

/*PC：フッターのレイアウト*/
.bottom {
  display: flex;
  background-color: var(--color-surface-muted);
  align-items: center;
  justify-content: space-between;
  height: 50px;
  z-index: 1;

  &.scriptVertical {
    background-color: var(--color-surface-base);
  }

  @include mq(sp) {
    height: 40px;
    background-color: var(--color-bg-dark);
  }

  .controller-container {
    display: flex;
    align-items: center;

    .multi-editor-link-wrapper {
      position: relative;
      display: inline-flex;
    }

    .multi-editor-link {
      padding: 10px;
    }

    .multi-editor-premium-badge {
      position: absolute;
      top: -4px;
      right: -4px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;

      img {
        width: 16px;
        height: 16px;
      }

      &:hover {
        filter: brightness(1.1);
      }

      &[data-tooltip]:hover::after {
        content: attr(data-tooltip);
        position: absolute;
        bottom: calc(100% + 8px);
        left: 50%;
        transform: translateX(-30%);
        z-index: 10;
        @include premium-tooltip;
      }
    }

    .show-preview-wrapper {
      position: relative;
      display: inline-flex;
    }

    .show-preview {
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
      border-radius: var(--radius-rounded);
      padding: 8px;
      cursor: pointer;
      color: var(--color-text-low);
      background-color: var(--color-surface-base);
      font-size: 16px;

      @include hover-overlay;

      &.isShowPreview {
        background-color: var(--color-primary);
        color: var(--color-text-on-fixed-bg);
      }
    }

    .premium-badge {
      position: absolute;
      top: -4px;
      right: -4px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;

      img {
        width: 16px;
        height: 16px;
      }

      &:hover {
        filter: brightness(1.1);
      }

      &[data-tooltip]:hover::after {
        content: attr(data-tooltip);
        position: absolute;
        bottom: calc(100% + 8px);
        left: 50%;
        transform: translateX(-30%);
        z-index: 10;
        @include premium-tooltip;
      }
    }

    .backup-button {
      display: flex;
      align-items: center;
      cursor: pointer;
      margin-right: 15px;

      img {
        width: 1rem;
        margin-right: 5px;
      }
    }

    /*モードセレクトボタンのレイアウト*/
    .mode-select {
      margin-right: 15px;

      select {
        padding: 7px 15px;
        border-radius: var(--radius-rounded);
      }
    }

    .text-counter {
      font-size: larger;
      margin: 15px;
      color: rgb(90, 90, 90);
      @include mq(sp) {
        position: fixed;
        right: 10px;
        bottom: 15px;
        padding: 2px 6px;
        background-color: rgba(255, 255, 255, 0.8);
        border-radius: var(--radius-sm);
        margin: 0;
        font-size: var(--font-size-xs);
        z-index: 999;
      }
    }
  }

  .setting-container {
    height: 100%;
    display: flex;
    align-items: center;
    position: relative;

    .dropup-menu {
      box-sizing: border-box;
      border-left: 1px solid var(--color-border);
    }
  }
}

// flex

.gap-1 {
  gap: 1rem;
}

// padding, margin

.ml-1 {
  margin-left: 1rem;
}

.vertical-hr {
  width: 1.2px;
  height: 40px;
  background-color: var(--color-inactive);
}

.editor-controller {
  position: relative;
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
  pointer-events: none;

  .icon-box {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 35px;
    box-sizing: border-box;
    padding: 8px;
    border-radius: var(--radius-sm);
    background-color: transparent;
    color: var(--color-text-normal);
    font-size: 16px;
    cursor: pointer;
    user-select: none;

    @include hover-overlay;
  }

  .insert-symbol {
    display: flex;
    align-items: center;
    pointer-events: auto;

    .icon-box {
      margin-right: 8px;

      &:last-of-type {
        margin-right: 0;
      }
    }
  }

  .undo-redo {
    display: flex;
    align-items: center;
    pointer-events: auto;

    .icon-box {
      margin-right: 8px;

      &:last-of-type {
        margin-right: 0;
      }
    }

    .icon-text {
      display: flex;
      flex-direction: column;
      align-items: center;

      img {
        width: 30px;
        height: 30px;
        margin-bottom: -8px;
      }

      div {
        font-size: var(--font-size-xxs);
        transform: scale(0.8);
      }
    }
  }

  .dialog {
    position: absolute;
    box-sizing: border-box;
    bottom: 60px;
    right: 134px;
    pointer-events: auto;
  }
}
</style>
